import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  type ApplicationConfig,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { PlatformAdminsEffects } from './+state/platform-admins/platform-admins.effects';
import {
  PLATFORM_ADMINS_FEATURE_KEY,
  platformAdminsReducer,
} from './+state/platform-admins/platform-admins.reducer';
import { PlatformIdentityEffects } from './+state/platform-identity/platform-identity.effects';
import {
  PLATFORM_IDENTITY_FEATURE_KEY,
  platformIdentityReducer,
} from './+state/platform-identity/platform-identity.reducer';
import { appRoutes } from './app.routes';
import { platformAuthInterceptor } from './common/interceptors/platform-auth-interceptor';
import { PlatformAuthService } from './common/services/platform-auth/platform-auth.service';

export function initializeAuthFactory() {
  const platformAuthService = inject(PlatformAuthService);
  return platformAuthService.initializeAuthState();
}

export const appConfig: ApplicationConfig = {
  providers: [
    // Core NgRx Store Setup
    provideStore({ router: routerReducer }),
    provideStoreDevtools({ logOnly: !isDevMode() }),
    provideRouterStore(),

    // Feature State + Effects
    provideState(PLATFORM_IDENTITY_FEATURE_KEY, platformIdentityReducer),
    provideState(PLATFORM_ADMINS_FEATURE_KEY, platformAdminsReducer),
    provideEffects([PlatformIdentityEffects, PlatformAdminsEffects]),

    // Angular essentials
    provideAnimations(),
    provideHttpClient(withInterceptors([platformAuthInterceptor])),

    // Firebase (shared SPOG project — required so the platformAdmin claim is visible)
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),

    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'always' },
    },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(appRoutes),

    // Registers onIdTokenChanged + gates bootstrap until first auth resolves.
    provideAppInitializer(initializeAuthFactory),
  ],
};
